'use client'

import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Stack, Code, Palette, Users, TrendUp, CheckCircle, Lightning, Target, BookOpen } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'

export default function PromoUI() {
  return (
    <div className="min-h-screen bg-[var(--brutal-paper)] relative">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc=')] opacity-30"
      />
      
      <Components.GlassNavigation />
      
      <main className="relative z-10 pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">


          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-6xl font-bold text-[var(--brutal-ink)] mb-6 leading-tight break-words">
              PromoUI Design System
            </h1>
            <p className="text-base md:text-xl text-[var(--brutal-muted)] mb-8 leading-relaxed max-w-4xl mx-auto">
              A comprehensive design system and component library that transformed development speed, reduced bugs by 44%, and enabled seamless brand refresh across the entire application.
            </p>
            <div className="flex flex-wrap gap-2 md:gap-3 justify-center mb-8 md:mb-12">
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Design Systems</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Component Library</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Storybook</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">Figma</Badge>
              <Badge variant="outline" className="border-[var(--brutal-ink)] text-[var(--brutal-ink)] bg-[var(--brutal-panel)]">MUI Integration</Badge>
            </div>
            
            {/* Hero Image — cover fill, anchored near top so chrome stays visible */}
            <div className="max-w-4xl mx-auto">
              <div className="brutal-panel rounded-2xl overflow-hidden">
                <Components.ImageModal
                  src="/assets/promoUI-design-system-card.png"
                  alt="PromoUI Design System Overview"
                  className="w-full"
                >
                  <div className="relative w-full aspect-[16/10] md:aspect-[16/9]">
                    <img
                      src="/assets/promoUI-design-system-card.png"
                      alt="PromoUI Design System Overview"
                      className="absolute inset-0 h-full w-full object-cover object-[center_25%]"
                      draggable={false}
                    />
                  </div>
                  <p className="brutal-label py-2 text-center md:hidden">Tap to enlarge</p>
                </Components.ImageModal>
              </div>
            </div>
          </div>

          <Components.KeyDecisions
            decisions={[
              {
                question: 'Build a custom system, or extend MUI?',
                tension:
                  'A custom kit would have matched every edge case. It also would have duplicated work MUI already solved, and slowed every brand refresh.',
                choice:
                  'We inventoried the app, then streamlined components into MUI so tokens and theming stayed one source of truth.',
              },
              {
                question: 'Document in Figma only, or ship a coded Storybook?',
                tension:
                  'Figma alone ships faster for designers. Engineering still rebuilt patterns by hand, and regressions kept slipping through.',
                choice:
                  'We paired Figma tokens with a Storybook code library so design and implementation stayed semantically aligned.',
              },
              {
                question: 'Treat UX bugs as polish, or as production defects?',
                tension:
                  'Experience issues were easy to defer behind feature work. That left users paying for the same failures sprint after sprint.',
                choice:
                  'We put a usability-bug process in place so experience failures were tracked and fixed like production defects.',
              },
            ]}
          />

          {/* Design System Components Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Design System Components</h2>
              <p className="text-[var(--brutal-muted)] text-base md:text-lg">
                Built with modern design tokens and semantic consistency across design and code
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="brutal-panel overflow-hidden">
                <Components.ImageDisplay
                  src="/assets/promoUI-design-tokens.png"
                  alt="PromoUI Design Tokens"
                  className="w-full h-44 sm:h-48 md:h-52"
                />
                <div className="p-4 md:p-6 text-center">
                  <h3 className="text-base md:text-lg font-bold text-[var(--brutal-ink)] mb-2">Design Tokens</h3>
                  <p className="text-[var(--brutal-ink)] text-xs md:text-sm">Semantic color, typography, and spacing tokens ensuring consistency across all components</p>
                </div>
              </div>
              
              <div className="brutal-panel overflow-hidden">
                <Components.ImageDisplay
                  src="/assets/promoUI-component-library.png"
                  alt="PromoUI Component Library"
                  className="w-full h-44 sm:h-48 md:h-52"
                />
                <div className="p-4 md:p-6 text-center">
                  <h3 className="text-base md:text-lg font-bold text-[var(--brutal-ink)] mb-2">Component Library</h3>
                  <p className="text-[var(--brutal-ink)] text-xs md:text-sm">Comprehensive set of reusable components built on MUI foundation with custom styling</p>
                </div>
              </div>
              
              <div className="brutal-panel overflow-hidden">
                <Components.ImageDisplay
                  src="/assets/promoai-storybook-2.png"
                  alt="PromoUI Code Integration"
                  className="w-full h-44 sm:h-48 md:h-52"
                />
                <div className="p-4 md:p-6 text-center">
                  <h3 className="text-base md:text-lg font-bold text-[var(--brutal-ink)] mb-2">Code Integration</h3>
                  <p className="text-[var(--brutal-ink)] text-xs md:text-sm">Seamless integration between design tokens and development with automated theming</p>
                </div>
              </div>
            </div>
          </div>

          {/* The Process Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">The Process</h2>
              <p className="text-[var(--brutal-muted)] text-base md:text-lg leading-relaxed max-w-4xl mx-auto">
                PromoUI design system was built using an inventory of the existing app styling, then streamlined to fit into the MUI design language it was based off of.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {[
                { n: '1', title: 'Inventory', body: 'Catalogued existing app styling patterns and components across the platform' },
                { n: '2', title: 'Streamline', body: 'Streamlined components to fit MUI design language and established consistency' },
                { n: '3', title: 'Interpret', body: 'Interpreted complex components using Telerik and established comprehensive roadmap' },
                { n: '4', title: 'Document', body: 'Created comprehensive documentation in Figma and Storybook for team adoption' },
              ].map(({ n, title, body }) => (
                <div
                  key={n}
                  className="brutal-panel p-4 md:p-6 h-full flex flex-row sm:flex-col items-start sm:items-center gap-4 sm:gap-0 sm:text-center"
                >
                  <div className="w-11 h-11 sm:w-12 sm:h-12 md:w-16 md:h-16 aspect-square brutal-chip bg-[var(--brutal-mustard)] text-[var(--brutal-ink)] shrink-0 sm:mx-auto sm:mb-3 md:mb-4">
                    <span className="font-bold text-sm md:text-base">{n}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base md:text-lg font-bold text-[var(--brutal-ink)] mb-1 sm:mb-2">{title}</h3>
                    <p className="text-[var(--brutal-muted)] text-xs md:text-sm leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Implementation Strategy Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Implementation Strategy</h2>
              <p className="text-[var(--brutal-muted)] text-base md:text-lg leading-relaxed max-w-4xl mx-auto">
                We documented and created a design system in Figma, so that all our designs used design tokens, then we went on to develop a code library that leveraged the design tokens and theming to ensure semantic consistency across design and code.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <div className="flex flex-col space-y-4 md:space-y-6">
                <div className="brutal-panel overflow-hidden flex flex-col h-full">
                  <Components.ImageDisplay
                    src="/assets/Promoui-table-docs.png"
                    alt="PromoUI Figma Documentation"
                    className="w-full h-44 sm:h-48 md:h-52"
                  >
                  </Components.ImageDisplay>
                  <div className="p-4 md:p-6 flex flex-col flex-grow">
                    <div className="flex items-center mb-3 md:mb-4">
                      <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-[var(--brutal-ink)] mr-2 md:mr-3" />
                      <h3 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)]">Figma Documentation</h3>
                    </div>
                    <p className="text-[var(--brutal-ink)] mb-3 md:mb-4 text-sm md:text-base">
                      Created comprehensive design system documentation in Figma with design tokens, component specifications, and usage guidelines.
                    </p>
                    <ul className="space-y-1 md:space-y-2 flex-grow">
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Design token library</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Component specifications</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Usage guidelines</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="brutal-panel overflow-hidden flex flex-col h-full">
                  <Components.ImageDisplay
                    src="/assets/promoui-storybook-design-tokens.png"
                    alt="PromoUI Storybook Implementation"
                    className="w-full h-44 sm:h-48 md:h-52"
                  >
                  </Components.ImageDisplay>
                  <div className="p-4 md:p-6 flex flex-col flex-grow">
                    <div className="flex items-center mb-3 md:mb-4">
                      <Code className="w-5 h-5 md:w-6 md:h-6 text-[var(--brutal-ink)] mr-2 md:mr-3" />
                      <h3 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)]">Storybook Implementation</h3>
                    </div>
                    <p className="text-[var(--brutal-ink)] mb-3 md:mb-4 text-sm md:text-base">
                      Built comprehensive documentation in Storybook with interactive component examples and code snippets.
                    </p>
                    <ul className="space-y-1 md:space-y-2 flex-grow">
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Interactive component demos</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Code examples</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Developer guidelines</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col space-y-4 md:space-y-6">
                <div className="brutal-panel overflow-hidden flex flex-col h-full">
                  <Components.ImageDisplay
                    src="/assets/richUI-plan.png"
                    alt="PromoUI Migration Strategy"
                    className="w-full h-44 sm:h-48 md:h-52"
                  >
                  </Components.ImageDisplay>
                  <div className="p-4 md:p-6 flex flex-col flex-grow">
                    <div className="flex items-center mb-3 md:mb-4">
                      <Target className="w-5 h-5 md:w-6 md:h-6 text-[var(--brutal-ink)] mr-2 md:mr-3" />
                      <h3 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)]">Migration Strategy</h3>
                    </div>
                    <p className="text-[var(--brutal-ink)] mb-3 md:mb-4 text-sm md:text-base">
                      Created a detailed plan and safeguarded approach for implementing these components across the app so that we could migrate current hard-coded screens to globally styled components.
                    </p>
                    <ul className="space-y-1 md:space-y-2 flex-grow">
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Phased rollout plan</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Risk mitigation strategies</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Component adoption tracking</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="brutal-panel overflow-hidden flex flex-col h-full">
                  <Components.ImageDisplay
                    src="/assets/Brand-Refresh-Migration.png"
                    alt="PromoUI Brand Refresh"
                    className="w-full h-44 sm:h-48 md:h-52"
                  >
                  </Components.ImageDisplay>
                  <div className="p-4 md:p-6 flex flex-col flex-grow">
                    <div className="flex items-center mb-3 md:mb-4">
                      <Palette className="w-5 h-5 md:w-6 md:h-6 text-[var(--brutal-ink)] mr-2 md:mr-3" />
                      <h3 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)]">Brand Refresh Ready</h3>
                    </div>
                    <p className="text-[var(--brutal-ink)] mb-3 md:mb-4 text-sm md:text-base">
                      With our established modular approach, we were ready to accept the refresh of our brand and restyle the app to match the new branding.
                    </p>
                    <ul className="space-y-1 md:space-y-2 flex-grow">
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Modular theming system</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Brand token integration</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-[var(--brutal-ink)]" />
                        <span className="text-[var(--brutal-muted)] text-xs md:text-sm">Seamless brand updates</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Business Impact Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Business Impact</h2>
              <p className="text-[var(--brutal-muted)] text-base md:text-lg leading-relaxed max-w-4xl mx-auto">
                Speed of development of new features increased dramatically once this was implemented. Both client users and our delivery team noticed a huge increase in productivity, learnability, and overall attitude toward the app.
              </p>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="brutal-panel p-4 md:p-6 text-center rounded-xl">
                <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[#8fce8f]">
                  <Lightning className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)]" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2">2x Faster</div>
                <div className="text-[var(--brutal-muted)] text-xs md:text-sm">Development Speed</div>
              </div>
              
              <div className="brutal-panel p-4 md:p-6 text-center rounded-xl">
                <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[#7eb8da]">
                  <TrendUp className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)]" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2">44% Reduction</div>
                <div className="text-[var(--brutal-muted)] text-xs md:text-sm">Regression Bugs</div>
              </div>
              
              <div className="brutal-panel p-4 md:p-6 text-center rounded-xl">
                <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[var(--brutal-mustard)]">
                  <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)]" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2">50% Fewer</div>
                <div className="text-[var(--brutal-muted)] text-xs md:text-sm">Usability Issues</div>
              </div>
              
              <div className="brutal-panel p-4 md:p-6 text-center rounded-xl">
                <div className="w-12 h-12 md:w-16 md:h-16 aspect-square brutal-chip mx-auto mb-3 md:mb-4 bg-[var(--brutal-accent)]">
                  <Users className="w-6 h-6 md:w-8 md:h-8 text-[var(--brutal-ink)]" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-1 md:mb-2">$2M+ ARR</div>
                <div className="text-[var(--brutal-muted)] text-xs md:text-sm">New Revenue</div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <div className="brutal-panel p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)] mb-3 md:mb-4">Development Improvements</h3>
                <div className="space-y-2 md:space-y-3">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-[var(--brutal-ink)]" />
                    <span className="text-[var(--brutal-ink)] text-sm md:text-base">Twice the speed of resolution for usability bugs</span>
                  </div>
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-[var(--brutal-ink)]" />
                    <span className="text-[var(--brutal-ink)] text-sm md:text-base">Half as many usability issues logged</span>
                  </div>
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-[var(--brutal-ink)]" />
                    <span className="text-[var(--brutal-ink)] text-sm md:text-base">Critical UI errors went from commonplace to rare</span>
                  </div>
                </div>
              </div>
              
              <div className="brutal-panel p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-semibold text-[var(--brutal-ink)] mb-3 md:mb-4">Team & Business Benefits</h3>
                <div className="space-y-2 md:space-y-3">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-[var(--brutal-ink)]" />
                    <span className="text-[var(--brutal-ink)] text-sm md:text-base">Easily trained and onboarded multiple interns and PMs</span>
                  </div>
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-[var(--brutal-ink)]" />
                    <span className="text-[var(--brutal-ink)] text-sm md:text-base">Huge increase in productivity and learnability</span>
                  </div>
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-[var(--brutal-ink)]" />
                    <span className="text-[var(--brutal-ink)] text-sm md:text-base">Generated over $2M in new ARR within few months</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Learnings Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4">Key Learnings</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="brutal-panel p-4 md:p-6">
                <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-2 md:mb-3">Foundation First</h4>
                <p className="text-[var(--brutal-ink)] text-sm md:text-base">Starting with a comprehensive inventory and aligning with established design languages like MUI created a solid foundation for scalable growth.</p>
              </div>
              
              <div className="brutal-panel p-4 md:p-6">
                <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-2 md:mb-3">Documentation Drives Adoption</h4>
                <p className="text-[var(--brutal-ink)] text-sm md:text-base">Dual documentation in Figma and Storybook ensured both designers and developers could easily adopt and contribute to the system.</p>
              </div>
              
              <div className="brutal-panel p-4 md:p-6">
                <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-2 md:mb-3">Migration Strategy Matters</h4>
                <p className="text-[var(--brutal-ink)] text-sm md:text-base">A careful, phased approach to migrating existing components prevented disruption while ensuring consistent implementation across the platform.</p>
              </div>
            </div>
          </div>

          {/* Call-to-Action Section */}
          <div className="text-center">
            <div className="brutal-panel p-4 md:p-8 rounded-xl">
              {/* Subtle divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-8"></div>
              <h3 className="text-xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-3 md:mb-4">Ready to scale your design system?</h3>
              <p className="text-[var(--brutal-muted)] mb-4 md:mb-8 max-w-3xl mx-auto text-sm md:text-lg">
                I help organizations build comprehensive design systems that accelerate development, reduce bugs, and enable seamless brand evolution.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Button 
                  asChild 
                  variant="outline" 
                  className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] hover:text-[var(--brutal-ink)] text-sm md:text-base px-3 md:px-6 py-2 md:py-3"
                >
                  <Link to="/ProfessionalProjects">View More Projects</Link>
                </Button>
                <Button 
                  asChild 
                  className="brutal-btn text-[var(--brutal-ink)] border-0 text-sm md:text-base px-3 md:px-6 py-2 md:py-3"
                >
                  <Link to="/Contact">Let's Connect</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}